import { HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import { MarketDivider } from '@market/react';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import {
  useGetOrderSummary,
  useUpdateOrderAddressInformation,
} from 'typescript/hooks/use_order_actions_client';
import { useToaster } from 'typescript/hooks/use_toaster';
import {
  ADDRESS_FIELDS,
  AddressType,
  getAddressPrefix,
} from 'typescript/schemas/address_forms/address_fields';
import useAddressForm from 'typescript/apps/checkout_v3/components/address_form/use_address_form';
import { useLocaleInfo } from 'typescript/context/locale_info';
import {
  useInvalidateQuery,
  useIsProcessingAnyRequest,
} from 'typescript/hooks/use_react_query_utils';
import useAddressValidation from 'typescript/hooks/use_address_validation_client';
import {
  isValidationSuccess,
  cleanAddressValidationResult,
} from 'typescript/utilities/address_validation';
import {
  ValidationRequestAddress,
  ValidationResponseAddress,
} from 'typescript/types/api/address_validation';
import { useFeatureFlags } from 'typescript/context/feature_flags';
import { QUERY_KEYS } from 'typescript/constants/react_query';
import ShippingRatesAndAddressValidation from 'typescript/apps/checkout_v3/components/delivery/ship_to_me/shipping_rates_address_validation';
import DeliveryPageCTA from 'typescript/apps/checkout_v3/components/delivery/delivery_page_cta';
import styles from 'typescript/apps/checkout_v3/components/delivery/ship_to_me/ship_to_me.module.scss';
import {
  onCheckoutPageViewCombined,
  onCheckoutPageClick,
} from 'typescript/utilities/tracking';
import {
  COMPONENT_NAMES,
  CHECKOUT_PAGE_CLICK_EVENTS,
} from 'typescript/constants/tracking';
import { hasShipAddress, isOrderFree } from 'typescript/utilities/order_data';
import usePoBox from 'typescript/apps/checkout_v3/components/delivery/ship_to_me/use_po_box';
import RecaptchaModal from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_modal';
import { fetchLocalizedComponent } from 'typescript/utilities/component_localization';
import { areFormFieldsValid } from 'typescript/utilities/address_utils';
import { captureMessage } from '@sentry/react';

const AddressFields = fetchLocalizedComponent(
  'typescript/apps/checkout_v3/components/address_form/address_fields/address_fields',
);

const ShipToMe = ({ className }: HTMLAttributes<HTMLElement>) => {
  const { t } = useTranslation();
  const { countryCode } = useLocaleInfo();
  const { orderNumber, trackingData } = useCheckoutFlow();
  const { showToast } = useToaster();

  const { isProcessing } = useIsProcessingAnyRequest();
  const { invalidate } = useInvalidateQuery();

  const [isFirstPrefill, setIsFirstPrefill] = useState(true);
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });
  const {
    updateOrderAddressInformation,
    isPending: isUpdateOrderAddressPending,
  } = useUpdateOrderAddressInformation({
    orderNumber,
  });

  const { addressValidationEnabled } = useFeatureFlags();
  const {
    validateAddress,
    reset: resetAddressValidation,
    data: addressValidationResult,
    isPending: isAddressValidationPending,
  } = useAddressValidation();

  const { formMethods, addressSchema } = useAddressForm({
    countryCode,
    addressType: AddressType.shipping,
  });

  const {
    control,
    formState: {
      isValid: isFormValid,
      isDirty: isFormDirty,
      touchedFields,
      dirtyFields,
    },
    getValues: getFormValues,
    setValue: setFormValue,
    reset: resetForm,
    watch: watchField,
    handleSubmit,
  } = formMethods;

  const { isPoBoxBlocked, showPoBoxWarning } = usePoBox({ orderNumber });

  const isAnyFieldTouched = Object.keys(touchedFields).length > 0;
  const showShippingRatesAddressValidation =
    touchedFields[ADDRESS_FIELDS.address_line_1] ||
    dirtyFields[ADDRESS_FIELDS.address_line_1] ||
    watchField(ADDRESS_FIELDS.address_line_1)?.length > 0;

  const updateAddress = (validationAddress?: ValidationResponseAddress) => {
    // the address will be a combination of the form data and the address validation result
    // since this will be used for any update call even if it doesn't go through validation
    const address = {
      ...getFormValues(),
      ...cleanAddressValidationResult(validationAddress),
    };

    updateOrderAddressInformation(
      { address },
      {
        onSuccess: () => {
          // invalidate the shipping rates query to refetch the rates with the new address
          invalidate(QUERY_KEYS.SHIPPING_RATES, { orderNumber });
          // clear the address validation result after updating the address
          resetAddressValidation();
        },
        onError: () => {
          showToast({
            message: t('checkout.delivery.toast.failedAddressUpdate'),
            variant: 'critical',
          });
        },
        onSettled: () => {
          // reset the form to clean all state and values
          resetForm(address);
        },
      },
    );
  };

  const handleAutoSubmit = () => {
    // if address validation is disabled, we'll just update the address with the form data
    // this also is a workaround for cases where address validation may mistakenly correct a valid address
    // and we don't want to block the user from proceeding (we set their merchant token in the LD flag)
    if (!addressValidationEnabled) {
      updateAddress();
      return;
    }

    const formData = getFormValues();

    // react-hook-form is a little flakey with the isValid state so we need to revalidate manually
    if (!areFormFieldsValid(formData, addressSchema)) {
      // this is temp for now, we'll migrate to using datadog logs once we have it set up
      captureMessage('Schema revalidation is invalid on address autosubmit', {
        extra: {
          orderNumber,
        },
      });

      return;
    }

    // we type assert the formData to ValidationRequestAddress since we know the form fields are valid
    // and we're only sending the address fields to the address validation API
    validateAddress(formData as ValidationRequestAddress, {
      onSuccess: (validationResult) => {
        // if the address is valid, we update the order with the new address
        // if the address is partially verified or failed verification, we no op here and show the address validation component
        if (isValidationSuccess(validationResult)) {
          updateAddress(validationResult.corrected_address);
        }
      },
      onError: () => {
        // if the api call fails, we update the order address with the form data to not block the user
        updateAddress();
      },
      onSettled: () => {
        // reset the formData so it's clean for the next form change
        resetForm(formData);
      },
    });
  };

  const handleSubmitWithLogging = (callback) => (event) => {
    onCheckoutPageClick({
      eventDesc: isOrderFree(orderSummaryData?.payment_due)
        ? CHECKOUT_PAGE_CLICK_EVENTS.delivery_page_complete_order
        : CHECKOUT_PAGE_CLICK_EVENTS.delivery_page_continue_to_payment,
      trackingData,
    });

    return handleSubmit(callback)(event);
  };

  // prefill form and update form values when order summary data changes
  useEffect(() => {
    if (!isFirstPrefill || !orderSummaryData) return;

    const {
      shipping_address: address,
      contact_information: contactInformation,
    } = orderSummaryData;

    const prefix = getAddressPrefix(AddressType.shipping);
    Object.entries({ ...address, ...contactInformation }).forEach(
      ([key, value]) => {
        setFormValue(`${prefix}${key}`, value, {
          shouldValidate: true, // only validate so the isValid state allows the user to continue
        });
      },
    );

    // we only want this prefill to happen once on the first load of the page
    setIsFirstPrefill(false);
  }, [orderSummaryData]);

  useEffect(() => {
    // isFormDity = true when any field is changed
    // isAnyFieldTouched = true when any field has been interacted with and clicked out of
    // isFormValid = true when all fields are valid
    if (isFormValid && isAnyFieldTouched && isFormDirty) {
      handleAutoSubmit();
    }
  }, [isFormValid, isAnyFieldTouched, isFormDirty]);

  useEffect(() => {
    onCheckoutPageViewCombined({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.SHIP_TO_ME,
      additionalParameters: trackingData,
    });
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        className={`shop-grid-container ${className}`}
        onSubmit={(e) => e.preventDefault()} // prevents submit button from reloading page
      >
        <RecaptchaModal handleFormSubmit={handleSubmitWithLogging} />
        <AddressFields />
        {showShippingRatesAddressValidation && (
          <div className="shop-grid-item-full shop-grid-container no-grid-gap">
            <h3
              className={`${styles.shippingSpeedHeader} shop-grid-item-full heading-20`}
            >
              {t('checkout.delivery.title.shipping')}
            </h3>
            <ShippingRatesAndAddressValidation
              showEmptyState={
                !isFormValid ||
                isAddressValidationPending ||
                isUpdateOrderAddressPending
              }
              showPoboxBlocked={isPoBoxBlocked && isFormValid}
              addressValidationResult={addressValidationResult}
              updateAddress={updateAddress}
            />
            <MarketDivider
              className={`shop-grid-item-full ${styles.divider}`}
              size="thin"
            />
            {showPoBoxWarning && !isProcessing && !addressValidationResult && (
              <div className="shop-grid-item-full shop-grid-container no-grid-gap">
                <p className={`shop-grid-item-full ${styles.poboxWarning}`}>
                  {t('checkout.delivery.shipping.poboxWarning')}
                </p>
                <MarketDivider
                  className={`shop-grid-item-full ${styles.divider}`}
                  size="thin"
                />
              </div>
            )}
          </div>
        )}
        <DeliveryPageCTA
          className="shop-grid-item-full"
          control={control}
          canContinue={
            isFormValid &&
            hasShipAddress(orderSummaryData?.shipping_address) &&
            !orderSummaryData.bopis_fulfillment_selected &&
            !isPoBoxBlocked &&
            !isProcessing
          }
          isLoading={isProcessing}
          handleSubmit={handleSubmitWithLogging}
        />
      </form>
    </FormProvider>
  );
};

export default ShipToMe;
